import React, { useRef, useEffect } from 'react';

const FaceCanvas = ({ imageUrl, faceShape, landmarks = null }) => {
  const canvasRef = useRef(null);
  
  // Function to draw face landmarks on canvas
  const drawFaceLandmarks = (ctx, image, landmarks) => {
    // Clear canvas
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Draw the image
    ctx.drawImage(image, 0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // If we have landmarks, draw them
    if (landmarks && landmarks.length > 0) {
      // This is a placeholder for actual landmark drawing
      // In a real implementation, you would use the landmark coordinates from TensorFlow.js
      
      // Example: Draw dots at landmark positions
      ctx.fillStyle = 'rgba(0, 255, 0, 0.6)';
      landmarks.forEach(point => {
        ctx.beginPath();
        ctx.arc(point.x * ctx.canvas.width, point.y * ctx.canvas.height, 2, 0, 2 * Math.PI);
        ctx.fill();
      });
    }
    
    // Draw face shape outline based on detected shape
    drawFaceShapeOutline(ctx, faceShape);
  };
  
  // Function to draw face shape outline
  const drawFaceShapeOutline = (ctx, shape) => {
    const width = ctx.canvas.width;
    const height = ctx.canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    
    ctx.strokeStyle = getFaceShapeColor(shape);
    ctx.lineWidth = 3;
    ctx.setLineDash([5, 5]);
    
    // Calculate face area - use a reasonable proportion of the canvas
    const faceWidth = width * 0.5;
    const faceHeight = height * 0.65;
    
    switch (shape) {
      case 'oval':
        // Draw oval shape
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, faceWidth / 2, faceHeight / 2, 0, 0, 2 * Math.PI);
        ctx.stroke();
        break;
        
      case 'round':
        // Draw round shape
        ctx.beginPath();
        const roundRadius = Math.min(faceWidth, faceHeight) / 2;
        ctx.arc(centerX, centerY, roundRadius, 0, 2 * Math.PI);
        ctx.stroke();
        break;
        
      case 'square':
        // Draw square shape
        ctx.beginPath();
        const squareSize = Math.min(faceWidth, faceHeight);
        ctx.rect(
          centerX - squareSize / 2, 
          centerY - squareSize / 2, 
          squareSize, 
          squareSize
        );
        ctx.stroke();
        break;
        
      case 'heart':
        // Draw heart shape (simplified)
        ctx.beginPath();
        // Top curves
        const heartWidth = faceWidth * 0.8;
        const heartHeight = faceHeight * 0.8;
        ctx.moveTo(centerX, centerY - heartHeight * 0.3);
        // Left curve
        ctx.bezierCurveTo(
          centerX - heartWidth / 2, centerY - heartHeight / 2,
          centerX - heartWidth / 2, centerY,
          centerX, centerY + heartHeight / 2
        );
        // Right curve
        ctx.bezierCurveTo(
          centerX + heartWidth / 2, centerY,
          centerX + heartWidth / 2, centerY - heartHeight / 2,
          centerX, centerY - heartHeight * 0.3
        );
        ctx.stroke();
        break;
        
      case 'diamond':
        // Draw diamond shape
        ctx.beginPath();
        ctx.moveTo(centerX, centerY - faceHeight / 2); // Top point
        ctx.lineTo(centerX + faceWidth / 2, centerY); // Right point
        ctx.lineTo(centerX, centerY + faceHeight / 2); // Bottom point
        ctx.lineTo(centerX - faceWidth / 2, centerY); // Left point
        ctx.closePath();
        ctx.stroke();
        break;
        
      case 'oblong':
        // Draw oblong shape
        ctx.beginPath();
        ctx.ellipse(
          centerX, 
          centerY, 
          faceWidth / 2.5, 
          faceHeight / 2, 
          0, 0, 2 * Math.PI
        );
        ctx.stroke();
        break;
        
      default:
        // No shape to draw
        break;
    }
  };
  
  // Get color for face shape
  const getFaceShapeColor = (shape) => {
    const colors = {
      oval: '#4ade80',      // Green
      round: '#3b82f6',     // Blue
      square: '#f59e0b',    // Amber
      heart: '#ec4899',     // Pink
      diamond: '#8b5cf6',   // Purple
      oblong: '#f43f5e',    // Rose
      default: '#6b7280'    // Gray
    };
    
    return colors[shape] || colors.default;
  };
  
  // Draw the image and face shape on canvas when component mounts or updates
  useEffect(() => {
    if (!canvasRef.current || !imageUrl) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = imageUrl;
    
    image.onload = () => {
      // Calculate canvas dimensions to maintain aspect ratio
      const containerWidth = canvas.parentElement.clientWidth;
      const aspectRatio = image.width / image.height;
      
      // Set canvas dimensions
      let canvasWidth = Math.min(containerWidth, 400); // Max width of 400px
      let canvasHeight = canvasWidth / aspectRatio;
      
      // If image is very tall, limit height
      if (canvasHeight > 500) {
        canvasHeight = 500;
        canvasWidth = canvasHeight * aspectRatio;
      }
      
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      
      // Draw image and face shape
      drawFaceLandmarks(ctx, image, landmarks);
    };
    
    image.onerror = () => {
      console.error('Error loading image on canvas');
    };
  }, [imageUrl, faceShape, landmarks]);
  
  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <h3 className="text-xl font-semibold mb-3 text-center">
        Face Shape: <span className={`face-shape-tag face-shape-${faceShape}`}>{faceShape}</span>
      </h3>
      
      <div className="flex justify-center">
        <canvas 
          ref={canvasRef} 
          className="max-w-full border border-gray-200 rounded-lg shadow-inner"
        ></canvas>
      </div>
      
      <p className="mt-4 text-sm text-center text-gray-600">
        {getShapeDescription(faceShape)}
      </p>
    </div>
  );
};

// Helper function to get description of face shape
const getShapeDescription = (shape) => {
  const descriptions = {
    oval: "Oval faces have balanced proportions with a slightly narrower forehead and jawline.",
    round: "Round faces have curved lines with the width and length in similar proportions and soft angles.",
    square: "Square faces have a strong jawline with a broad forehead and angular features.",
    heart: "Heart-shaped faces have a wider forehead and cheekbones with a narrow jawline and pointed chin.",
    diamond: "Diamond faces have narrow foreheads and jawlines with the cheekbones as the widest point.",
    oblong: "Oblong faces are longer than they are wide with a long, straight cheek line.",
    default: "Face shape characteristics will appear here."
  };
  
  return descriptions[shape] || descriptions.default;
};

export default FaceCanvas;